import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import type * as React from "react";
import { Table } from "@/components/ui/table";
import { cn } from "@/lib/utils";

export type DataTableDensity = "compact" | "standard" | "rich";

const TABLE_DENSITY_STYLES: Record<DataTableDensity, string> = {
  compact: "[&_thead_th]:h-[31px] [&_tbody_td]:py-2",
  standard: "[&_thead_th]:h-[34px] [&_tbody_td]:py-3",
  rich: "[&_thead_th]:h-[34px] [&_tbody_td]:py-4",
};

interface DataTableProps extends React.ComponentProps<typeof Table> {
  density?: DataTableDensity;
  stickyHeader?: boolean;
}

/**
 * Product-level table styling. Row height is content-driven: density controls
 * vertical breathing room while avatars, media, and secondary labels determine
 * the final height.
 */
export function DataTable({
  density = "standard",
  stickyHeader = false,
  className,
  ...props
}: DataTableProps): React.ReactElement {
  return (
    <Table
      className={cn(
        "text-[11px] [&_thead_tr]:h-auto [&_thead_th]:bg-subtle [&_thead_th]:px-3.5 [&_thead_th]:py-0 [&_thead_th]:text-[9px] [&_thead_th]:font-semibold [&_thead_th]:uppercase [&_thead_th]:leading-3 [&_thead_th]:tracking-[0.05em] [&_thead_th]:text-muted-foreground [&_tbody_tr]:h-auto [&_tbody_td]:px-3.5",
        TABLE_DENSITY_STYLES[density],
        stickyHeader &&
          "[&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-subtle",
        className,
      )}
      {...props}
    />
  );
}

interface DataTableDisclosureProps
  extends Omit<React.ComponentProps<"button">, "children" | "onClick"> {
  expanded: boolean;
  label: string;
  onToggle: () => void;
}

/** Compact, accessible disclosure control for hierarchical table rows. */
export function DataTableDisclosure({
  expanded,
  label,
  onToggle,
  className,
  ...props
}: DataTableDisclosureProps): React.ReactElement {
  return (
    <button
      type="button"
      data-slot="data-table-disclosure"
      aria-label={label}
      aria-expanded={expanded}
      className={cn(
        "flex size-[22px] shrink-0 items-center justify-center rounded-sm text-ink hover:bg-subtle",
        className,
      )}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
      {...props}
    >
      {expanded ? (
        <IconChevronDown className="size-3.5" aria-hidden />
      ) : (
        <IconChevronRight className="size-3.5" aria-hidden />
      )}
    </button>
  );
}

interface DataTableFrameProps extends React.ComponentProps<"section"> {
  toolbar?: React.ReactNode;
  footer?: React.ReactNode;
  viewportClassName?: string;
}

export function DataTableFooter({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      data-slot="data-table-footer"
      className={cn(
        "flex h-10 shrink-0 items-center justify-between gap-3 border-t border-line bg-table-footer px-3.5 text-[9px] leading-3 text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

/** Shared Paper table surface: 48px toolbar, 14px radius, and 40px footer. */
export function DataTableFrame({
  toolbar,
  footer,
  viewportClassName,
  className,
  children,
  ...props
}: DataTableFrameProps): React.ReactElement {
  return (
    <section
      className={cn("overflow-hidden rounded-[14px] border border-line bg-surface", className)}
      {...props}
    >
      {toolbar ? (
        <div className="flex min-h-12 items-center justify-between gap-3 border-b border-line px-3.5">
          {toolbar}
        </div>
      ) : null}
      <div
        data-slot="data-table-viewport"
        className={cn(
          "min-h-0 overflow-auto [&>[data-slot=table-container]]:overflow-visible",
          viewportClassName,
        )}
      >
        {children}
      </div>
      {footer ? <DataTableFooter>{footer}</DataTableFooter> : null}
    </section>
  );
}

export type DataGridDensity = "compact" | "standard" | "relaxed" | "rich";

const GRID_ROW_DENSITY_STYLES: Record<DataGridDensity, string> = {
  compact: "min-h-11 py-2",
  standard: "min-h-[54px] py-3",
  relaxed: "min-h-[60px] py-3.5",
  rich: "min-h-[66px] py-3.5",
};

export function DataGridBody({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      data-slot="data-grid-body"
      className={cn("[&>*:last-child]:border-b-0", className)}
      {...props}
    />
  );
}

interface DataGridHeaderProps extends React.ComponentProps<"div"> {
  density?: "compact" | "standard";
}

export function DataGridHeader({
  density = "compact",
  className,
  ...props
}: DataGridHeaderProps): React.ReactElement {
  return (
    <div
      className={cn(
        "grid items-center border-b border-line bg-subtle px-3.5 text-[9px] font-semibold uppercase leading-3 tracking-[0.05em] text-muted-foreground",
        density === "compact" ? "min-h-[31px]" : "min-h-[34px]",
        className,
      )}
      {...props}
    />
  );
}

interface DataGridRowProps extends React.ComponentProps<"div"> {
  density?: DataGridDensity;
}

/** Use for semantic row roots such as TanStack Links or native buttons. */
export function dataGridRowClassName(
  density: DataGridDensity = "standard",
  className?: string,
): string {
  return cn(
    "grid items-center border-b border-line px-3.5 text-[10px] transition-colors last:border-b-0 hover:bg-table-row-hover",
    GRID_ROW_DENSITY_STYLES[density],
    className,
  );
}

export function DataGridRow({
  density = "standard",
  className,
  ...props
}: DataGridRowProps): React.ReactElement {
  return <div className={dataGridRowClassName(density, className)} {...props} />;
}
